<template>
  <div id="app">
    <template v-if="loading">
      <Loader fullscreen />
    </template>
    <template v-else-if="authorized">
      <Header @signout="handleSignOut"/>
      <router-view @signout="handleSignOut"/>
      <Footer/>
    </template>
    <template v-else-if="!authorized && !isPublic">
      <Login :reset.sync="isReset" @signedin="handleSignedIn"/>
    </template>
    <template v-else>
      <router-view />
    </template>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import Header from '@/components/layout/Header';
  import Footer from '@/components/layout/Footer';
  import Loader from '@/components/ui/Loader';
  import Login from '@/views/Login';
  import DomUtil from '@/utils/DomUtil';
  import DBStorage from '@/utils/DBStorage';
  import Crypto from '@/utils/Crypto';
  import { Store } from '@/store';

  export default {
    name: 'App',
    components: {
      Header,
      Footer,
      Loader,
      Login,
    },
    //---------------------------------------------------
    //
    //  Properties
    //
    //---------------------------------------------------
    props: {
    },
    //---------------------------------------------------
    //
    //  Data Model
    //
    //---------------------------------------------------
    data() {
      return {
        loading: true,
        authorized: false,
        isPublic: true,
        nextRevalidation: 0,
      };
    },
    //---------------------------------------------------
    //
    //  Watch Properties
    //
    //---------------------------------------------------
    watch: {
      auth(value, old) {
        if (old && value === null) {
          Store.clear();
          this.authorized = false;
        }
      },
      $route: {
        handler(value) {
          this.isPublic = value.meta?.public === true;
        },
        immediate: true,
      },
    },
    //---------------------------------------------------
    //
    //  Computed Properties
    //
    //---------------------------------------------------
    computed: {
      ...mapGetters({ auth: 'auth' }),
      isReset() {
        return this.$route.name === 'index-reset';
      },
    },
    //---------------------------------------------------
    //
    //  Vue Lifecycle
    //
    //---------------------------------------------------
    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    mounted() {
      DBStorage.init();
      document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
      document.documentElement.style.setProperty('--sbw', `${DomUtil.defaultScrollbarWidth}px`);
      window.addEventListener('focus', this.revalidateAuth);
      this.validateAuth(this.auth);
    },
    // beforeUpdate() {},
    // updated() {},
    beforeDestroy() {
      window.removeEventListener('focus', this.revalidateAuth);
    },
    // destroyed() {},
    //---------------------------------------------------
    //
    //  Methods
    //
    //---------------------------------------------------
    methods: {
      async validateAuth(auth) {
        if (auth) {
          const response = await this.$sav.api.get('/auth/validate');
          if (response) {
            this.handleSignedIn(response);
          } else {
            this.handleSignOut();
          }
        }
        this.loading = false;
      },
      async revalidateAuth() {
        const now = Date.now();
        if (!document.hidden && this.authorized && this.nextRevalidation < now) {
          const response = await this.$sav.api.get('/auth/validate');
          if (response) {
            this.handleSignedIn(response);
          } else {
            this.handleSignOut();
          }
        }
      },

      //----------------------------------
      // Event Handlers
      //----------------------------------
      async handleSignedIn(response) {
        if (response?.uuid && response?.role && response?.permissions) {
          Store.permissions = response.permissions;
          Store.role = response.role;
          Store.name = response.name;
          Store.extras = response.extras;
          Store.uuid = response.uuid;
          Store.branch = response.branch;
          Store.skey = await Crypto.importSecret(response.skey);
          this.authorized = true;
          this.nextRevalidation = Date.now() + 60000;
        } else {
          this.handleSignOut();
        }
      },
      async handleSignOut() {
        await this.$sav.api.get('/auth/signout');
        Store.clear();
        if (this.$route.path !== '/') {
          this.$router.push('/');
        }
        this.authorized = false;
      },
    },
  };
</script>

<style lang="scss">
  @import '~scss/app';
  @import '~scss/mixins';
  @import '~scss/variables';

  #app {
    position: relative;
    min-height: 100vh;
    width: 100%;
  }

  main.view {
    padding: 48px 40px;

    & > .descriptor {
      margin-bottom: 48px;
      p {
        @include font-body-large;
        color: $color-black-alpha-80;
        margin: 24px 0;
      }
    }
  }
</style>
